import { Input as AntInput, InputProps } from 'antd';
import styled, { css } from 'styled-components';
import { colors } from '@10d/tend-ui-tokens/samolet';

import { CommonInputProps, CommonSpecialProps, CommonTextAreaProps } from './types';

import Resizer from 'assets/img/resizer.svg';

export const labelStyle = css`
  font-size: 12px;
  line-height: 20px;
  color: ${colors.gray900};
  margin-bottom: 2px;
`;

export const captionStyle = css`
  font-size: 12px;
  line-height: 20px;
  margin-top: 2px;
`;

const defaultStyle = css<{ size?: InputProps['size'] }>`
  border-radius: 8px;
  border-color: #bdc5db;
  color: ${colors.gray900};
  line-height: 24px;

  &:not(.ant-input-textarea):not(textarea) {
    padding: 3px 7px;
    height: ${props => (props.size === 'small' ? 24 : 40)}px;

    &.ant-input-affix-wrapper-sm {
      padding: 0 7px;
    }
  }

  .ant-input-suffix {
    margin-left: 8px;
  }

  .ant-input-clear-icon {
    path:first-child {
      fill: ${colors.gray400};
      inset-block-start: 3px !important;
    }
  }

  .ant-input {
    line-height: 24px;
    color: ${colors.gray900};
  }

  &:hover,
  &:focus,
  &.ant-input-affix-wrapper-focused {
    border-color: ${colors.blue500};
  }

  &:focus,
  &.ant-input-affix-wrapper-focused {
    box-shadow: 0 0 0 3px ${colors.blue100};
  }

  & .ant-input::placeholder,
  &::placeholder {
    color: ${colors.gray400};
  }
`;

const textareaWithCount = css`
  &:after {
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
    color: ${colors.gray400};
  }

  textarea {
    ${defaultStyle}
  }
`;

export const disabledStyle = css`
  background: #eaedf5;
  border-color: #bdc5db;
  color: ${colors.gray400};
`;

// Стили для Input при наличии пропса error
const errorStyle = css`
  border-color: ${colors.red500};

  &-focused,
  &:focus {
    border-color: ${colors.red600};
    border-right-width: 1px !important;
    outline: 0;
    box-shadow: 0 0 0 2px ${colors.red600}20;
  }
`;

export const Input = styled(AntInput)<CommonSpecialProps<CommonInputProps>>`
  ${defaultStyle}

  &.ant-input-affix-wrapper-disabled,
  & .ant-input-disabled {
    ${disabledStyle}
  }

  &&.ant-input-affix-wrapper {
    ${({ $hasError }) => $hasError && errorStyle};
  }

  input:not(input:hover) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const Search = styled(AntInput.Search)<CommonSpecialProps<CommonInputProps>>`
  .ant-input-group .ant-input-affix-wrapper:not(:last-child) {
    ${defaultStyle}
  }

  &&&& .ant-input-affix-wrapper-disabled {
    ${disabledStyle}
  }

  &&&& .ant-input-affix-wrapper {
    ${({ $hasError }) => $hasError && errorStyle};
  }

  .ant-input-prefix {
    margin-right: 8px;

    .anticon {
      color: ${colors.gray400};
      font-size: 16px;
    }
  }

  .ant-input-group-addon {
    display: none;
  }
`;

export const TextArea = styled(AntInput.TextArea)<CommonSpecialProps<CommonTextAreaProps>>`
  ${defaultStyle}

  ${({ showCount }) => (showCount ? textareaWithCount : defaultStyle)}

  &::-webkit-resizer,
  textarea::-webkit-resizer {
    background: url(${Resizer}) no-repeat bottom right;
  }

  &.ant-input[disabled] {
    ${disabledStyle}
  }

  .ant-input {
    line-height: 16px;
    font-size: 14px !important;
    overflow: hidden;
  }

  ${({ $hasError }) => $hasError && errorStyle};
`;

export const Label = styled.div`
  ${labelStyle}
`;

export const CaptionText = styled.div<{ $hasError?: boolean }>`
  color: ${({ $hasError }) => ($hasError ? colors.red500 : colors.gray650)};
  ${captionStyle}
`;

export default Input;
